import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import api from '../../api/api';
import { Product, ProductCategory, Supplier, SupplierProduct } from '../../types';

interface ProductState {
  products: Product[];
  categories: ProductCategory[];
  suppliers: Supplier[];
  supplierProducts: SupplierProduct[];
  supplierSearchProducts: Product[];
  supplierDataGroupId?: string | null;
  supplierDataEpoch: number;
  supplierRequestId?: string;
  supplierProductsRequestId?: string;
  supplierSearchProductsRequestId?: string;
  loading: boolean;
}

const initialState: ProductState = {
  products: [],
  categories: [],
  suppliers: [],
  supplierProducts: [],
  supplierSearchProducts: [],
  supplierDataEpoch: 0,
  loading: false,
};
type BulkDeleteResult = { deletedIds: number[]; errors: string[] };
type SupplierDataContext = { groupId: string | null; epoch: number };

export const setSupplierDataGroup = createAction<string | null>('product/setSupplierDataGroup');
export const invalidateSupplierDataContext = createAction('product/invalidateSupplierDataContext');

export const fetchProducts = createAsyncThunk('product/fetchAll', async () => {
  const res = await api.get<Product[]>('/products');
  return res.data;
});

export const createProduct = createAsyncThunk('product/create', async (data: any) => {
  const res = await api.post<Product>('/products', data);
  return res.data;
});

export const updateProduct = createAsyncThunk(
  'product/update',
  async ({ id, data }: { id: number; data: any }) => {
    const res = await api.put<Product>(`/products/${id}`, data);
    return res.data;
  }
);

export const deleteProduct = createAsyncThunk<number, number, { rejectValue: any }>(
  'product/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/products/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? { message: err?.message ?? 'Не вдалося видалити товар' });
    }
  }
);

export const bulkDeleteProducts = createAsyncThunk('product/bulkDelete', async (ids: number[]) => {
  const res = await api.post<BulkDeleteResult>('/products/bulk-delete', { ids });
  return res.data;
});

export const fetchProductCategories = createAsyncThunk('product/fetchCategories', async () => {
  const res = await api.get<ProductCategory[]>('/productcategories');
  return res.data;
});

export const createProductCategory = createAsyncThunk('product/createCategory', async (data: any) => {
  const res = await api.post<ProductCategory>('/productcategories', data);
  return res.data;
});

export const updateProductCategory = createAsyncThunk(
  'product/updateCategory',
  async ({ id, data }: { id: number; data: any }) => {
    const res = await api.put<ProductCategory>(`/productcategories/${id}`, data);
    return res.data;
  }
);

export const deleteProductCategory = createAsyncThunk('product/deleteCategory', async (id: number) => {
  await api.delete(`/productcategories/${id}`);
  return id;
});

export const fetchSuppliers = createAsyncThunk('product/fetchSuppliers', async (_context?: SupplierDataContext) => {
  const res = await api.get<Supplier[]>('/suppliers');
  return res.data;
});

export const createSupplier = createAsyncThunk(
  'product/createSupplier',
  async ({ data }: { data: any; groupId: string | null; epoch: number }) => {
    const res = await api.post<Supplier>('/suppliers', data);
    return res.data;
  }
);

export const updateSupplier = createAsyncThunk(
  'product/updateSupplier',
  async ({ id, data }: { id: number; data: any; groupId: string | null; epoch: number }) => {
    const res = await api.put<Supplier>(`/suppliers/${id}`, data);
    return res.data;
  }
);

export const deleteSupplier = createAsyncThunk<
  number,
  { id: number; groupId: string | null; epoch: number },
  { rejectValue: string }
>(
  'product/deleteSupplier',
  async ({ id }, { rejectWithValue }) => {
    try {
      await api.delete(`/suppliers/${id}`);
      return id;
    } catch (error) {
      if (!isAxiosError<{ message?: string }>(error)) throw error;
      return rejectWithValue(error.response?.data?.message ?? 'Не вдалося видалити постачальника.');
    }
  }
);

// ── Supplier Products ──────────────────────────────────────────────────────
export const fetchSupplierProducts = createAsyncThunk(
  'product/fetchSupplierProducts',
  async (params?: SupplierDataContext & { supplierId?: number; internalProductId?: number }) => {
    const queryParams = params && Object.entries(params)
      .filter(([key, value]) => key !== 'groupId' && key !== 'epoch' && value != null);
    const query = queryParams?.length
      ? '?' + queryParams.map(([key, value]) => `${key}=${value}`).join('&')
      : '';
    const res = await api.get<SupplierProduct[]>(`/supplierproducts${query}`);
    return res.data;
  }
);

export const createSupplierProduct = createAsyncThunk(
  'product/createSupplierProduct',
  async ({ data }: { data: any; groupId: string | null; epoch: number }) => {
    const res = await api.post<SupplierProduct>('/supplierproducts', data);
    return res.data;
  }
);

export const updateSupplierProduct = createAsyncThunk(
  'product/updateSupplierProduct',
  async ({ id, data }: { id: number; data: any; groupId: string | null; epoch: number }) => {
    const res = await api.put<SupplierProduct>(`/supplierproducts/${id}`, data);
    return res.data;
  }
);

export const deleteSupplierProduct = createAsyncThunk(
  'product/deleteSupplierProduct',
  async ({ id }: { id: number; groupId: string | null; epoch: number }) => {
    await api.delete(`/supplierproducts/${id}`);
    return id;
  }
);

export const importSupplierPriceList = createAsyncThunk(
  'product/importSupplierPriceList',
  async ({ supplierId, rows }: { supplierId: number; rows: any[]; groupId: string | null; epoch: number }) => {
    const res = await api.post(`/supplierproducts/import/${supplierId}`, rows);
    return res.data;
  }
);

export const fetchSupplierSearchProducts = createAsyncThunk(
  'product/fetchSupplierSearchProducts',
  async (_context: SupplierDataContext) => {
    const res = await api.get<Product[]>('/products');
    return res.data;
  }
);

export const recalculateProductPrices = createAsyncThunk(
  'product/recalculatePrices',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.post('/products/recalculate-prices');
      return res.data as { updated: number };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err?.message || 'Не вдалося перерахувати ціни');
    }
  }
);

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(setSupplierDataGroup, (state, action) => {
        state.supplierDataGroupId = action.payload;
        state.supplierDataEpoch += 1;
        state.supplierRequestId = undefined;
        state.supplierProductsRequestId = undefined;
        state.supplierSearchProductsRequestId = undefined;
        state.suppliers = [];
        state.supplierProducts = [];
        state.supplierSearchProducts = [];
      })
      .addCase(invalidateSupplierDataContext, (state) => {
        state.supplierDataGroupId = undefined;
        state.supplierDataEpoch += 1;
        state.supplierRequestId = undefined;
        state.supplierProductsRequestId = undefined;
        state.supplierSearchProductsRequestId = undefined;
        state.suppliers = [];
        state.supplierProducts = [];
        state.supplierSearchProducts = [];
      })
      .addCase(fetchProducts.pending, (state) => { state.loading = true; })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.products = action.payload;
        state.loading = false;
      })
      .addCase(fetchProducts.rejected, (state) => { state.loading = false; })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.products.push(action.payload);
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        const idx = state.products.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.products[idx] = action.payload;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter((i) => i.id !== action.payload);
      })
      .addCase(bulkDeleteProducts.fulfilled, (state, action) => {
        const deleted = new Set(action.payload.deletedIds);
        state.products = state.products.filter((i) => !deleted.has(i.id));
      })
      .addCase(fetchProductCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(createProductCategory.fulfilled, (state, action) => {
        state.categories.push(action.payload);
      })
      .addCase(updateProductCategory.fulfilled, (state, action) => {
        const idx = state.categories.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.categories[idx] = action.payload;
      })
      .addCase(deleteProductCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter((i) => i.id !== action.payload);
      })
      .addCase(fetchSuppliers.pending, (state, action) => {
        if (action.meta.arg &&
            action.meta.arg.groupId === state.supplierDataGroupId &&
            action.meta.arg.epoch === state.supplierDataEpoch)
          state.supplierRequestId = action.meta.requestId;
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        if (!action.meta.arg) {
          if (state.supplierDataGroupId !== undefined) return;
          state.suppliers = action.payload;
          return;
        }
        if (action.meta.arg.groupId !== state.supplierDataGroupId ||
            action.meta.arg.epoch !== state.supplierDataEpoch ||
            action.meta.requestId !== state.supplierRequestId) return;
        state.suppliers = action.payload;
        state.supplierRequestId = undefined;
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        if (action.meta.arg &&
            action.meta.arg.groupId === state.supplierDataGroupId &&
            action.meta.arg.epoch === state.supplierDataEpoch &&
            action.meta.requestId === state.supplierRequestId)
          state.supplierRequestId = undefined;
      })
      .addCase(createSupplier.fulfilled, (state, action) => {
        if (action.meta.arg.groupId !== state.supplierDataGroupId ||
            action.meta.arg.epoch !== state.supplierDataEpoch) return;
        state.suppliers.push(action.payload);
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        if (action.meta.arg.groupId !== state.supplierDataGroupId ||
            action.meta.arg.epoch !== state.supplierDataEpoch) return;
        const idx = state.suppliers.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.suppliers[idx] = action.payload;
      })
      .addCase(deleteSupplier.fulfilled, (state, action) => {
        if (action.meta.arg.groupId !== state.supplierDataGroupId ||
            action.meta.arg.epoch !== state.supplierDataEpoch) return;
        state.suppliers = state.suppliers.filter((i) => i.id !== action.payload);
      })
      .addCase(fetchSupplierProducts.pending, (state, action) => {
        const { groupId, epoch } = action.meta.arg ?? {};
        if (groupId === state.supplierDataGroupId && epoch === state.supplierDataEpoch)
          state.supplierProductsRequestId = action.meta.requestId;
      })
      .addCase(fetchSupplierProducts.fulfilled, (state, action) => {
        const { groupId, epoch } = action.meta.arg ?? {};
        if (!action.meta.arg) {
          if (state.supplierDataGroupId !== undefined) return;
          state.supplierProducts = action.payload;
          return;
        }
        if (groupId !== state.supplierDataGroupId ||
            epoch !== state.supplierDataEpoch ||
            action.meta.requestId !== state.supplierProductsRequestId) return;
        state.supplierProducts = action.payload;
        state.supplierProductsRequestId = undefined;
      })
      .addCase(fetchSupplierProducts.rejected, (state, action) => {
        const { groupId, epoch } = action.meta.arg ?? {};
        if (action.meta.arg &&
            groupId === state.supplierDataGroupId &&
            epoch === state.supplierDataEpoch &&
            action.meta.requestId === state.supplierProductsRequestId)
          state.supplierProductsRequestId = undefined;
      })
      .addCase(fetchSupplierSearchProducts.pending, (state, action) => {
        if (action.meta.arg.groupId === state.supplierDataGroupId &&
            action.meta.arg.epoch === state.supplierDataEpoch)
          state.supplierSearchProductsRequestId = action.meta.requestId;
      })
      .addCase(fetchSupplierSearchProducts.fulfilled, (state, action) => {
        if (action.meta.arg.groupId !== state.supplierDataGroupId ||
            action.meta.arg.epoch !== state.supplierDataEpoch ||
            action.meta.requestId !== state.supplierSearchProductsRequestId) return;
        state.supplierSearchProducts = action.payload;
        state.supplierSearchProductsRequestId = undefined;
      })
      .addCase(fetchSupplierSearchProducts.rejected, (state, action) => {
        if (action.meta.arg.groupId === state.supplierDataGroupId &&
            action.meta.arg.epoch === state.supplierDataEpoch &&
            action.meta.requestId === state.supplierSearchProductsRequestId)
          state.supplierSearchProductsRequestId = undefined;
      })
      .addCase(createSupplierProduct.fulfilled, (state, action) => {
        if (action.meta.arg.groupId !== state.supplierDataGroupId ||
            action.meta.arg.epoch !== state.supplierDataEpoch) return;
        state.supplierProducts.push(action.payload);
      })
      .addCase(updateSupplierProduct.fulfilled, (state, action) => {
        if (action.meta.arg.groupId !== state.supplierDataGroupId ||
            action.meta.arg.epoch !== state.supplierDataEpoch) return;
        const idx = state.supplierProducts.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.supplierProducts[idx] = action.payload;
      })
      .addCase(deleteSupplierProduct.fulfilled, (state, action) => {
        if (action.meta.arg.groupId !== state.supplierDataGroupId ||
            action.meta.arg.epoch !== state.supplierDataEpoch) return;
        state.supplierProducts = state.supplierProducts.filter((i) => i.id !== action.payload);
      });
  },
});

export default productSlice.reducer;
